import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Banner from "@/components/Banner";
import Breadcrumb from "@/components/Breadcrumb";
import Button from "@/components/Button";
import EmptyState from "@/components/EmptyState";
import PageHeading from "@/components/PageHeading/PageHeading";
import { TableHeading } from "@/components/Table";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import pluralize from "@/lib/pluralize";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import AppLayout from "@/layouts/AppLayout";
import { useNavigate } from "@/router";
import { useMembershipServicesNavLinks } from "../../../_membershipServicesNavLinks";
import { MEMBER_ELECTIONS_PAST_PATH, MEMBER_ELECTIONS_PAST_PRESIDENTS_PATH } from "../constants";
import {
  GENERATE_ELECTION_PAST_PRESIDENTS_ROSTER_MUTATION,
  REMOVE_ELECTION_PAST_PRESIDENT_ASSIGNEE_MUTATION
} from "../mutations";
import { ELECTION_PAST_PRESIDENT_ASSIGNEES_QUERY, type ElectionPastPresidentAssigneeNode } from "../queries";
import AddPastPresidentDialog from "./_components/AddPastPresidentDialog";
import PastPresidentAssigneesTable from "./_components/PastPresidentAssigneesTable";
import RemovePastPresidentDialog from "./_components/RemovePastPresidentDialog";
import { formatPastPresidentsElectionSummary } from "./_utils/formatPastPresidentsElectionSummary";
import { formatPastPresidentsRosterCount } from "./_utils/formatPastPresidentsRosterCount";
import {
  parsePastPresidentsRosterSearch,
  type PastPresidentsRosterNavigationState
} from "./_utils/pastPresidentsRosterNavigation";

export default function PastPresidentAssigneesPage() {
  usePageTitle(MEMBER_ELECTIONS_PAST_PRESIDENTS_PATH, "People | Past Presidents Roster");
  const navigate = useNavigate();
  const location = useLocation();
  const navLinks = useMembershipServicesNavLinks();
  const downloadContext = useMemo(() => parsePastPresidentsRosterSearch(location.search), [location.search]);
  const navigationState = location.state as PastPresidentsRosterNavigationState | null;
  const electionRuns = navigationState?.electionRuns ?? [];
  const hasDownloadContext = Boolean(downloadContext.electionRunId || downloadContext.electionRunIds?.length);

  const canManageApplications = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [assigneeToRemove, setAssigneeToRemove] = useState<ElectionPastPresidentAssigneeNode | null>(null);
  const [busyAction, setBusyAction] = useState<"remove" | "download" | null>(null);

  const { data, loading, error } = useQuery(ELECTION_PAST_PRESIDENT_ASSIGNEES_QUERY, {
    skip: !canManageApplications,
    fetchPolicy: "network-only"
  });

  const assignees = data?.electionPastPresidentAssignees ?? [];
  const assigneeIds = useMemo(
    () => data?.electionPastPresidentAssignees?.map((row) => row.id) ?? [],
    [data?.electionPastPresidentAssignees]
  );
  const isBusy = busyAction != null || loading;
  const rosterCountLabel = formatPastPresidentsRosterCount(assignees.length);

  const [removeAssignee] = useMutation(REMOVE_ELECTION_PAST_PRESIDENT_ASSIGNEE_MUTATION, {
    refetchQueries: [{ query: ELECTION_PAST_PRESIDENT_ASSIGNEES_QUERY }],
    awaitRefetchQueries: true
  });
  const [generateRoster] = useMutation(GENERATE_ELECTION_PAST_PRESIDENTS_ROSTER_MUTATION);

  const runCount = downloadContext.electionRunIds?.length ?? (downloadContext.electionRunId ? 1 : 0);

  const defaultBody =
    "Add and edit Past Presidents used when splitting newly elected members across assignees for Past Presidents election roster exports.";
  const downloadBody =
    runCount > 0
      ? `This export splits newly elected members across Past Presidents for ${runCount} completed ${pluralize(runCount, "election run")}. Add or remove names below, then download.`
      : defaultBody;

  const electionSummary = electionRuns.length > 0 ? formatPastPresidentsElectionSummary(electionRuns) : undefined;

  const openAdd = useCallback(() => setAddDialogOpen(true), []);
  const closeAdd = useCallback(() => setAddDialogOpen(false), []);
  const closeRemove = useCallback(() => {
    if (busyAction === "remove") return;
    setAssigneeToRemove(null);
  }, [busyAction]);

  const handleRemoveConfirm = useCallback(async () => {
    if (!assigneeToRemove || busyAction) return;

    setBusyAction("remove");
    try {
      const { data: mutationData } = await removeAssignee({
        variables: { input: { id: assigneeToRemove.id } }
      });
      const result = mutationData?.removeElectionPastPresidentAssignee;
      if (!result?.success) {
        showErrorSnackbar("Could not remove Past President", { subtitle: result?.message || "Please try again." });
        return;
      }
      showSnackbar("Past President removed");
      setAssigneeToRemove(null);
    } catch {
      showErrorSnackbar("Could not remove Past President", { subtitle: "Please try again." });
    } finally {
      setBusyAction(null);
    }
  }, [assigneeToRemove, busyAction, removeAssignee]);

  const handleDownload = useCallback(async () => {
    if (busyAction || assigneeIds.length === 0 || !hasDownloadContext) return;

    setBusyAction("download");
    try {
      const input =
        downloadContext.electionRunIds && downloadContext.electionRunIds.length > 0
          ? {
              electionRunIds: downloadContext.electionRunIds,
              pastPresidentAssigneeIds: assigneeIds
            }
          : {
              electionRunId: downloadContext.electionRunId,
              pastPresidentAssigneeIds: assigneeIds
            };

      const { data: mutationData } = await generateRoster({ variables: { input } });
      const result = mutationData?.generateElectionPastPresidentsRoster;

      if (!result?.success || !result.url) {
        showErrorSnackbar("Past Presidents roster not generated", {
          subtitle: result?.message || "Please try again later."
        });
        return;
      }

      showSnackbar(result.message || "Export generated");
      window.location.assign(result.url);
    } catch {
      showErrorSnackbar("Past Presidents roster not generated", { subtitle: "Please try again later." });
    } finally {
      setBusyAction(null);
    }
  }, [assigneeIds, busyAction, downloadContext, generateRoster, hasDownloadContext]);

  // Always return to Past Elections. The member-elections index redirects to Pending Electees.
  const backLink = { to: MEMBER_ELECTIONS_PAST_PATH } as const;

  const headingActions = canManageApplications
    ? [
        {
          label: "Add Past President",
          icon: "add" as const,
          variant: "filled" as const,
          onClick: openAdd,
          disabled: isBusy,
          testId: "add-past-president"
        },
        ...(hasDownloadContext
          ? [
              {
                label: "Download roster",
                icon: "download" as const,
                variant: "outlined" as const,
                onClick: handleDownload,
                disabled: isBusy || assignees.length === 0,
                testId: "past-presidents-roster-download"
              }
            ]
          : [])
      ]
    : [];

  return (
    <AppLayout interior bottomBar navLinks={navLinks}>
      <Breadcrumb title="Member Elections" compactTitle="Past Presidents Roster" backLink={backLink} />
      <PageHeading title="Past Presidents Roster" compact />
      <div className="w-full">
        <TableHeading
          heading="Past Presidents Roster"
          body={hasDownloadContext ? downloadBody : defaultBody}
          actions={headingActions}
        />

        {!canManageApplications ? (
          <EmptyState
            iconName="lock"
            title="You Don't Have Access to This Page"
            caption="Managing Past Presidents roster assignees requires the manage applications permission. Contact your administrator if you need access."
          />
        ) : error ? (
          <EmptyState iconName="error" title="Error" caption="An error occurred while loading Past Presidents." />
        ) : (
          <div className="flex flex-col gap-6">
            {hasDownloadContext && electionSummary ? (
              <Banner variant="info" description={electionSummary} hideSecondaryDismiss />
            ) : null}
            <PastPresidentAssigneesTable
              loading={loading}
              assignees={assignees}
              busy={isBusy}
              rosterCountLabel={rosterCountLabel}
              onAdd={openAdd}
              onRemove={setAssigneeToRemove}
            />
            {hasDownloadContext ? (
              <div className="flex justify-end">
                <Button
                  variant="text"
                  onClick={() => navigate(MEMBER_ELECTIONS_PAST_PATH)}
                  disabled={isBusy}
                  testId="past-presidents-roster-cancel"
                >
                  Back to past elections
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {canManageApplications ? (
        <>
          <AddPastPresidentDialog open={addDialogOpen} onClose={closeAdd} />
          <RemovePastPresidentDialog
            open={Boolean(assigneeToRemove)}
            assignee={assigneeToRemove}
            saving={busyAction === "remove"}
            onClose={closeRemove}
            onConfirm={handleRemoveConfirm}
          />
        </>
      ) : null}
    </AppLayout>
  );
}
